



import React, { useState, useEffect, useRef } from 'react'
import { Link, useNavigate } from 'react-router-dom'
import { FaShoppingCart, FaHeart, FaRegHeart } from "react-icons/fa";
import Footer from './component/Footer';
import api from './api';
import { useWishlist } from "./context/WishlistContext";
import { useCart } from "./context/CartContext";
import { toast, ToastContainer } from "react-toastify";

const WishLisht = () => {
    const { addToCart, updateQuantity, removeFromCart, cart } = useCart();
    const navigate = useNavigate();
    const { wishlistCount, setWishlistCount } = useWishlist();

    const [wishlist, setWishlist] = useState([]);
    const [wishlistedProducts, setWishlistedProducts] = useState({});
    const [loading, setLoading] = useState(true);
    const [isVisible, setIsVisible] = useState(false);

    const handleScrollTop = () => window.scrollTo({ top: 0, behavior: 'smooth' });

    // ─── Cart Quantities (mirrors ArrivalCart exactly) ────────────────────────
    const [productQuantities, setProductQuantities] = useState(() => {
        try {
            const saved = localStorage.getItem("cartQuantities");
            if (saved) {
                const parsed = JSON.parse(saved);
                if (parsed && typeof parsed === "object") return parsed;
            }
        } catch (e) { }
        return {};
    });

    const productQuantitiesRef = useRef(productQuantities);

    const updateProductQty = (updater) => {
        setProductQuantities((prev) => {
            const next = typeof updater === "function" ? updater(prev) : updater;
            productQuantitiesRef.current = next;
            try { localStorage.setItem("cartQuantities", JSON.stringify(next)); } catch (e) { }
            return next;
        });
    };

    // Seed from cart context
    useEffect(() => {
        if (cart.length === 0) return;
        updateProductQty((prev) => {
            const next = { ...prev };
            const cartIdSet = new Set(cart.map((i) => i.id || i._id || i.productId));
            Object.keys(next).forEach((id) => { if (!cartIdSet.has(id)) delete next[id]; });
            cart.forEach((item) => {
                const id = item.id || item._id || item.productId;
                if (id && next[id] === undefined) next[id] = item.quantity || 1;
            });
            return next;
        });
    }, [cart]);
    // ─────────────────────────────────────────────────────────────────────────

    useEffect(() => {
        window.scrollTo({ top: 0, behavior: 'auto' });
    }, []);

    useEffect(() => {
        const toggleVisibility = () => setIsVisible(window.pageYOffset > 300);
        window.addEventListener('scroll', toggleVisibility);
        return () => window.removeEventListener('scroll', toggleVisibility);
    }, []);

    // ─── Fetch Wishlist ────────────────────────────────────────────────────────
    useEffect(() => {
        const fetchWishlist = async () => {
            setLoading(true);
            try {
                const storedData = localStorage.getItem("userData");
                const token = storedData ? JSON.parse(storedData).token : null;

                if (!token) {
                    setWishlist([]);
                    setWishlistCount(0);
                    setWishlistedProducts({});
                    setLoading(false);
                    return;
                }

                const res = await api.get("/user/get-wishlists", {
                    headers: { Authorization: `Bearer ${token}` },
                });

                if (res.status === 200) {
                    const wishlistData = res.data.products || [];
                    setWishlist(wishlistData);
                    setWishlistCount(res.data.wishlistCount || wishlistData.length || 0);

                    const productStates = {};
                    wishlistData.forEach((item) => { productStates[item._id] = true; });
                    setWishlistedProducts(productStates);
                } else {
                    setWishlist([]);
                    setWishlistCount(0);
                    setWishlistedProducts({});
                }
            } catch (err) {
                console.error("Error fetching wishlist:", err);
                setWishlist([]);
                setWishlistCount(0);
                setWishlistedProducts({});
            } finally {
                setLoading(false);
            }
        };
        fetchWishlist();
    }, []);
    // ─────────────────────────────────────────────────────────────────────────

    // ─── Remove / Toggle Wishlist ──────────────────────────────────────────────
    const removeWishList = async (productId) => {
        try {
            const storedData = localStorage.getItem("userData");
            const token = storedData ? JSON.parse(storedData).token : null;
            if (!token) return;

            const res = await api.put(
                "/user/add-wishlist",
                { productId },
                { headers: { Authorization: `Bearer ${token}` } }
            );

            if (res.status === 200) {
                setWishlistedProducts((prev) => { const u = { ...prev }; delete u[productId]; return u; });
                setWishlist((prev) => prev.filter((item) => item._id !== productId));
                setWishlistCount((prev) => (prev > 0 ? prev - 1 : 0));
                toast.info("Removed from wishlist", { position: "top-right", autoClose: 2000, theme: "dark" });
            }
        } catch (err) {
            console.error("Error removing wishlist:", err);
        }
    };
    // ─────────────────────────────────────────────────────────────────────────

    // ─── Add to Cart ──────────────────────────────────────────────────────────
    const handleAddToCart = async (product) => {
        const storedData = localStorage.getItem("userData");
        const token = storedData ? JSON.parse(storedData).token : null;
        if (!token) { navigate("/user-login"); return; }

        const selectedColor = product?.variants?.[0]?.color?.name;
        const selectedSize = product?.variants?.[0]?.sizes?.[0]?.size;

        const selectedStock = product?.variants?.[0]?.sizes?.[0]?.stock;

        if (selectedStock <= 0) {
            toast.warning("This Size is out of stock. Please Select another Size", {
                position: "top-right",
                autoClose: 2000,
                theme: "dark",
            });
            return;
        }

        await addToCart(product, selectedColor, selectedSize, "1");
        updateProductQty((prev) => ({ ...prev, [product._id]: 1 }));
        toast.success("Added to cart successfully", { position: "top-right", autoClose: 3000, theme: "dark" });
    };
    // ─────────────────────────────────────────────────────────────────────────

    // ─── Increase Quantity ────────────────────────────────────────────────────
    const handleIncreaseQuantity = async (product) => {
        const storedData = localStorage.getItem("userData");
        const token = storedData ? JSON.parse(storedData).token : null;
        if (!token) { navigate("/user-login"); return; }

        const currentQty = productQuantitiesRef.current[product._id] || 1;
        const maxStock = product?.variants?.[0]?.sizes?.[0]?.stock || 99;
        if (currentQty >= maxStock) {
            toast.warning("Maximum stock limit reached", { position: "top-right", autoClose: 2000, theme: "dark" });
            return;
        }
        const newQty = currentQty + 1;
        updateProductQty((prev) => ({ ...prev, [product._id]: newQty }));
        await updateQuantity(product, product?.variants?.[0]?.color?.name, product?.variants?.[0]?.sizes?.[0]?.size, newQty);
    };
    // ─────────────────────────────────────────────────────────────────────────

    // ─── Decrease Quantity ────────────────────────────────────────────────────
    const handleDecreaseQuantity = async (product) => {
        const storedData = localStorage.getItem("userData");
        const token = storedData ? JSON.parse(storedData).token : null;
        if (!token) { navigate("/user-login"); return; }

        const currentQty = productQuantitiesRef.current[product._id] || 1;
        const newQty = currentQty - 1;
        if (newQty <= 0) {
            const cartItem = cart.find((item) => (item.id || item._id) === product._id);
            if (cartItem) await removeFromCart(cartItem.id || cartItem._id);
            updateProductQty((prev) => { const u = { ...prev }; delete u[product._id]; return u; });
            return;
        }
        updateProductQty((prev) => ({ ...prev, [product._id]: newQty }));
        await updateQuantity(product, product?.variants?.[0]?.color?.name, product?.variants?.[0]?.sizes?.[0]?.size, newQty);
    };
    // ─────────────────────────────────────────────────────────────────────────

    // ─── Buy Now ──────────────────────────────────────────────────────────────
    const handleBuyNow = (product) => {
        const storedData = localStorage.getItem("userData");
        const token = storedData ? JSON.parse(storedData).token : null;

        const selectedColor = product?.variants?.[0]?.color?.name;
        const selectedSize = product?.variants?.[0]?.sizes?.[0]?.size;
        const quantity = "1";

        const selectedStock = product?.variants?.[0]?.sizes?.[0]?.stock;

        if (selectedStock <= 0) {
            toast.warning("This Size is out of stock. Please Select another Size", {
                position: "top-right",
                autoClose: 2000,
                theme: "dark",
            });
            return;
        }

        const selectedVariant = getSelectedVariant(product, selectedColor, selectedSize);
        if (!selectedVariant) {
            console.error("Selected variant not found!");
            return;
        }

        const updatedProduct = {
            ...product,
            variant: selectedVariant,
            quantity: quantity,
        };

        if (!token) { navigate("/user-login"); return; }
        navigate("/check-out", {
            state: { token, products: updatedProduct, singleProduct: "yes", singleProductId: product._id },
        });
    };
    // ─────────────────────────────────────────────────────────────────────────

    function goToProductDetails(product) {
        navigate(`/product-details/${product._id}`, { state: { items: product } });
    }

    const getSelectedVariant = (product, selectedColor, selectedSize) => {

        if (!product?.variants) return null;

        // Find the color variant
        const colorVariant = product.variants.find(
            (v) => v.color.name === selectedColor
        );
        // console.log("varient data >>>>>",product.variants);
        // console.log("selectedColor data >>>>>",selectedColor);

        if (!colorVariant) return null;



        // Find the size variant inside that color
        const sizeVariant = colorVariant.sizes.find(
            (s) => s.size === selectedSize
        );
        if (!sizeVariant) return null;

        return {
            color: colorVariant.color,  // {name: 'Black', value: '#000000'}
            size: sizeVariant.size,     // 'M'
            originalPrice: sizeVariant.originalPrice,
            discountedPrice: sizeVariant.discountedPrice,
            stock: sizeVariant.stock,
            images: colorVariant.images, // optional, if needed
        };
    };

    return (
        <>
            <ToastContainer />

            <style>{`
                .wl-grid {
                    display: grid;
                    grid-template-columns: repeat(5, 1fr);
                    gap: 20px;
                    padding: 24px 16px;
                }
                @media (max-width: 1280px) { .wl-grid { grid-template-columns: repeat(4, 1fr); } }
                @media (max-width: 1024px) { .wl-grid { grid-template-columns: repeat(3, 1fr); } }
                @media (max-width: 768px)  { .wl-grid { grid-template-columns: repeat(2, 1fr); gap: 12px; padding: 12px 8px; } }
                @media (max-width: 480px)  { .wl-grid { grid-template-columns: repeat(2, 1fr); gap: 10px; } }

                .wl-card {
                    border: 3px solid #FFD700;
                    border-radius: 6px;
                    background: #fff;
                    display: flex;
                    flex-direction: column;
                    min-height: 370px;
                    transition: transform 0.3s ease, box-shadow 0.3s ease;
                    overflow: hidden;
                }
                .wl-card:hover {
                    transform: translateY(-8px);
                    box-shadow: 0 8px 20px rgba(0,0,0,0.2);
                }

                .wl-img-wrap {
                    width: 100%;
                    height: 200px;
                    overflow: hidden;
                    position: relative;
                    border-radius: 2px;
                    cursor: pointer;
                }
                @media (max-width: 768px) { .wl-img-wrap { height: 220px; } }

                .wl-img-wrap img {
                    width: 100%; height: 100%;
                    object-fit: cover; object-position: center;
                    display: block;
                }

                .wl-content {
                    flex: 1;
                    display: flex;
                    flex-direction: column;
                    padding: 10px 8px;
                }

                .wl-qty-wrap {
                    display: flex;
                    align-items: center;
                    border: 1.5px solid #ddd;
                    border-radius: 5px;
                    overflow: hidden;
                    flex: 1;
                }
                .wl-qty-btn {
                    background: #f5f5f5;
                    border: none;
                    padding: 0 10px;
                    height: 34px;
                    font-size: 16px;
                    cursor: pointer;
                    transition: background 0.2s;
                }
                .wl-qty-btn:hover { background: #e0e0e0; }
                .wl-qty-display {
                    flex: 1;
                    text-align: center;
                    font-weight: 600;
                    font-size: 14px;
                }
            `}</style>

            <main className="main__content_wrapper">

                {/* Wishlist Grid */}
                <section className="product__section section--padding pt-0">
                    <div className="container-fluid">
                        {loading ? (
                            <div className="wl-grid">
                                {Array.from({ length: 10 }).map((_, i) => (
                                    <div key={i} className="wl-card" style={{ minHeight: 370 }}>
                                        <div style={{ width: "100%", height: 200, background: "linear-gradient(90deg,#f0f0f0 25%,#e8e8e8 50%,#f0f0f0 75%)", backgroundSize: "200% 100%", animation: "shimmer 1.2s infinite" }} />
                                        <div style={{ padding: 10 }}>
                                            <div style={{ height: 14, width: "70%", background: "#f0f0f0", borderRadius: 4, marginBottom: 8 }} />
                                            <div style={{ height: 12, width: "40%", background: "#f0f0f0", borderRadius: 4 }} />
                                        </div>
                                        <style>{`@keyframes shimmer{0%{background-position:200% 0}100%{background-position:-200% 0}}`}</style>
                                    </div>
                                ))}
                            </div>
                        ) : wishlist.length > 0 ? (
                            <div className="wl-grid">
                                {wishlist.map((product) => {
                                    const allOutOfStock = product.variants?.every((v) =>
                                        v.sizes?.every((s) => s.stock === 0)
                                    );
                                    const isInCart = productQuantities.hasOwnProperty(product._id);
                                    const qty = productQuantities[product._id] || 0;
                                    const isWishlisted = !!wishlistedProducts[product._id];

                                    const discountedPrice = product.variants?.[0]?.sizes?.[0]?.discountedPrice;
                                    const originalPrice = product.variants?.[0]?.sizes?.[0]?.originalPrice || 0;
                                    const discount = product.variants?.[0]?.sizes?.[0]?.discount;
                                    const pricesDiffer = discountedPrice && discountedPrice !== originalPrice;

                                    return (
                                        <div key={product._id} className="wl-card">
                                            {/* Image */}
                                            <div className="wl-img-wrap" onClick={() => goToProductDetails(product)}>
                                                <img
                                                    src={product.variants?.[0]?.images?.[0] ?? "https://via.placeholder.com/300x300?text=No+Image"}
                                                    alt={product.title || "Product"}
                                                />

                                                {allOutOfStock && (
                                                    <span style={{ position: "absolute", top: 10, left: 10, background: "rgba(255,255,255,0.9)", color: "red", fontWeight: "bold", fontSize: 12, padding: "3px 8px", borderRadius: 3 }}>
                                                        Out of Stock
                                                    </span>
                                                )}

                                                {/* Wishlist (heart) button */}
                                                <button
                                                    onClick={(e) => { e.stopPropagation(); removeWishList(product._id); }}
                                                    style={{ position: "absolute", top: 10, right: 10, background: isWishlisted ? "rgba(237,46,46,0.95)" : "#CEB256", border: "none", borderRadius: "50%", width: 32, height: 32, display: "flex", justifyContent: "center", alignItems: "center", cursor: "pointer", boxShadow: "0 2px 6px rgba(0,0,0,0.15)", transition: "all 0.3s" }}
                                                >
                                                    <FaHeart style={{ color: "white", fontSize: 16 }} />
                                                </button>

                                                {/* Rating badge */}
                                                <div style={{ position: "absolute", bottom: 8, left: 8, background: "rgba(0,0,0,0.6)", color: "white", padding: "3px 6px", borderRadius: 4, fontSize: 11, display: "flex", alignItems: "center", gap: 3 }}>
                                                    ⭐ {product.averageRating?.toFixed(1) || "0.0"}
                                                </div>
                                            </div>

                                            {/* Content */}
                                            <div className="wl-content">
                                                <div style={{ flex: 1, marginBottom: 8 }}>
                                                    <span style={{ display: "block", fontSize: 14, fontWeight: 700, color: "#333", marginBottom: 4, lineHeight: 1.3, whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>
                                                        {product.name || product.title}
                                                    </span>

                                                    <p style={{ fontSize: 11, color: "#666", marginBottom: 6, lineHeight: 1.4 }}>
                                                        {product.description
                                                            ? product.description.split(" ").length > 4
                                                                ? product.description.split(" ").slice(0, 4).join(" ") + "..."
                                                                : product.description
                                                            : ""}
                                                    </p>
                                                    <span
                                                        style={{
                                                            display: "block",
                                                            fontSize: "14px",
                                                        }}
                                                    >Size : {product.variants?.[0]?.sizes?.[0]?.size}, Color : {product.variants?.[0]?.color?.name}, Stock : {product.variants?.[0]?.sizes?.[0]?.stock}</span>
                                                    {/* Price */}
                                                    <div style={{ display: "flex", alignItems: "center", flexWrap: "wrap", gap: 5, marginBottom: 8 }}>
                                                        {pricesDiffer ? (
                                                            <>
                                                                <span style={{ color: "#28a745", fontWeight: 700, fontSize: 15 }}>₹{Number(discountedPrice)}</span>
                                                                <span style={{ color: "#999", fontSize: 13, textDecoration: "line-through" }}>₹{originalPrice}</span>
                                                                {discount && (
                                                                    <span style={{ color: "black", padding: "2px 6px", borderRadius: 3, fontSize: 10, fontWeight: 600 }}>
                                                                        {discount}% OFF
                                                                    </span>
                                                                )}
                                                            </>
                                                        ) : (
                                                            <span style={{ color: "#28a745", fontWeight: 700, fontSize: 15 }}>₹{originalPrice}</span>
                                                        )}
                                                    </div>
                                                </div>

                                                {/* Buttons */}
                                                <div style={{ marginTop: "auto", paddingTop: 8 }}>
                                                    {isInCart && !allOutOfStock ? (
                                                        <div style={{ display: "flex", flexDirection: "row", gap: 8, width: "100%", alignItems: "center" }}>
                                                            {/* Qty controls */}
                                                            <div className="wl-qty-wrap">
                                                                <button className="wl-qty-btn" onClick={(e) => { e.stopPropagation(); handleDecreaseQuantity(product); }}>−</button>
                                                                <span className="wl-qty-display">{qty}</span>
                                                                <button className="wl-qty-btn" onClick={(e) => { e.stopPropagation(); handleIncreaseQuantity(product); }}>+</button>
                                                            </div>
                                                            {/* Buy Now */}
                                                            <button
                                                                onClick={(e) => { e.stopPropagation(); handleBuyNow(product); }}
                                                                style={{ flex: 1, backgroundColor: "#28a745", padding: "8px", borderRadius: 5, color: "#fff", border: "none", fontSize: 11, fontWeight: 600, cursor: "pointer", whiteSpace: "nowrap" }}
                                                            >
                                                                Buy Now
                                                            </button>
                                                        </div>
                                                    ) : (
                                                        <div style={{ display: "flex", gap: 6, width: "100%" }}>
                                                            <button
                                                                onClick={(e) => { e.stopPropagation(); handleAddToCart(product); }}
                                                                disabled={allOutOfStock}
                                                                style={{ flex: 1, padding: "8px 4px", backgroundColor: allOutOfStock ? "#ccc" : "#CEB256", color: "white", border: "none", borderRadius: 5, fontSize: 11, fontWeight: 600, cursor: allOutOfStock ? "not-allowed" : "pointer", transition: "all 0.3s ease", whiteSpace: "nowrap" }}
                                                            >
                                                                Add to Cart
                                                            </button>
                                                            <button
                                                                onClick={(e) => { e.stopPropagation(); handleBuyNow(product); }}
                                                                disabled={allOutOfStock}
                                                                style={{ flex: 1, padding: "8px 4px", backgroundColor: allOutOfStock ? "#ccc" : "#28a745", color: "white", border: "none", borderRadius: 5, fontSize: 11, fontWeight: 600, cursor: allOutOfStock ? "not-allowed" : "pointer", transition: "all 0.3s ease", whiteSpace: "nowrap" }}
                                                            >
                                                                Buy Now
                                                            </button>
                                                        </div>
                                                    )}
                                                </div>
                                            </div>
                                        </div>
                                    );
                                })}
                            </div>
                        ) : (
                            <div style={{ textAlign: "center", padding: "80px 20px" }}>
                                <div style={{ fontSize: 48, marginBottom: 16 }}>❤️</div>
                                <h3 style={{ marginBottom: 8 }}>No Saved Items!</h3>
                                <p style={{ color: "#888", marginBottom: 24 }}>Go to home and add some products to your wishlist.</p>
                                <Link to="/" style={{ backgroundColor: "#CEB256", color: "#fff", padding: "10px 24px", borderRadius: 6, textDecoration: "none", fontWeight: 600 }}>
                                    Explore Products
                                </Link>
                            </div>
                        )}
                    </div>
                </section>
            </main>

            <Footer />

            {/* Scroll to top */}
            <button
                onClick={handleScrollTop}
                style={{ position: "fixed", bottom: 30, right: 30, width: 50, height: 50, borderRadius: "50%", backgroundColor: "#ceb256", color: "#fff", border: "none", zIndex: 1000, cursor: "pointer", display: isVisible ? "flex" : "none", alignItems: "center", justifyContent: "center", boxShadow: "0 4px 10px rgba(0,0,0,0.2)" }}
            >
                <svg xmlns="http://www.w3.org/2000/svg" height="24" width="24" viewBox="0 0 512 512" fill="none" stroke="currentColor" strokeWidth="48" strokeLinecap="round" strokeLinejoin="round">
                    <path d="M112 244l144-144 144 144" />
                    <path d="M256 120v292" />
                </svg>
            </button>
        </>
    );
};

export default WishLisht;